import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaBell, FaComments } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { PocketBaseService } from '@/services/pocketbase.service';

const pb = PocketBaseService.getInstance().getPocketBase();

interface NotificationItem {
  id: string;
  chatId: string;
  unreadCount: number;
  name: string;
  avatarUrl?: string;
}

interface ChatInfoResponse {
  other_user_name?: string;
  other_user_avatar_url?: string;
}

function getInitials(name?: string | null): string {
  if (!name?.trim()) {
    return '?';
  }
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase();
}

export function NotificationsBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchBadgeCount = () => {
    const userId = pb.authStore.record?.id;
    if (!userId) {
      setUnreadCount(0);
      return;
    }
    pb.collection('chat_notifications')
      .getFullList({ filter: `user_id="${userId}"` })
      .then((rows) => {
        const total = rows.reduce((sum, row) => sum + (row.unread_count ?? 0), 0);
        setUnreadCount(total);
      })
      .catch(() => {
        setUnreadCount(0);
      });
  };

  const fetchNotificationItems = () => {
    const userId = pb.authStore.record?.id;
    if (!userId) {
      setItems([]);
      return;
    }
    setIsLoading(true);
    pb.collection('chat_notifications')
      .getFullList({
        filter: `user_id="${userId}" && unread_count > 0`,
        sort: '-updated',
      })
      .then(async (rows) => {
        const nextItems = await Promise.all(
          rows.map(async (row) => {
            const unread = row.unread_count ?? 0;
            try {
              const info: ChatInfoResponse = await pb.send(`/api/chat-info/${row.chat_id}`, {
                method: 'GET',
              });
              return {
                id: row.id,
                chatId: row.chat_id as string,
                unreadCount: unread,
                name: info.other_user_name?.trim() || t('notifications-panel-unknown-user'),
                avatarUrl: info.other_user_avatar_url,
              } satisfies NotificationItem;
            } catch {
              return {
                id: row.id,
                chatId: row.chat_id as string,
                unreadCount: unread,
                name: t('notifications-panel-unknown-user'),
              } satisfies NotificationItem;
            }
          })
        );
        setItems(nextItems);
        setUnreadCount(nextItems.reduce((sum, item) => sum + item.unreadCount, 0));
      })
      .catch(() => {
        setItems([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchBadgeCount();
    const interval = setInterval(fetchBadgeCount, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }
    fetchNotificationItems();
    const onPointerDown = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount);

  const openChat = (chatId: string) => {
    setOpen(false);
    navigate(`/chats/${chatId}`);
  };

  return (
    <div className={`notifications-bell-wrap ${open ? 'is-open' : ''}`} ref={wrapRef}>
      <button
        type="button"
        className={`notifications-bell ${open ? 'is-active' : ''}`}
        onClick={() => setOpen((value) => !value)}
        aria-label={t('app-header-notifications-aria-label', { count: unreadCount })}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <FaBell aria-hidden size={16} />
        {unreadCount > 0 && <span className="notifications-bell__badge">{badgeLabel}</span>}
      </button>

      {open && (
        <div
          className="notifications-panel"
          role="dialog"
          aria-label={t('notifications-panel-title')}
        >
          <div className="notifications-panel__header">
            <h3>{t('notifications-panel-title')}</h3>
            {unreadCount > 0 && (
              <span className="notifications-panel__count">
                {t('notifications-panel-unread-count', { count: unreadCount })}
              </span>
            )}
          </div>

          <div className="notifications-panel__body">
            {isLoading ? (
              <div className="notifications-panel__empty">
                <p>{t('notifications-panel-loading')}</p>
              </div>
            ) : items.length === 0 ? (
              <div className="notifications-panel__empty">
                <span className="notifications-panel__empty-icon" aria-hidden>
                  <FaComments size={22} />
                </span>
                <p>{t('notifications-panel-empty')}</p>
              </div>
            ) : (
              <ul className="notifications-panel__list">
                {items.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      className="notifications-panel__item"
                      onClick={() => openChat(item.chatId)}
                    >
                      {item.avatarUrl ? (
                        <img className="notifications-panel__avatar" src={item.avatarUrl} alt="" />
                      ) : (
                        <span className="notifications-panel__avatar is-fallback" aria-hidden>
                          {getInitials(item.name)}
                        </span>
                      )}
                      <span className="notifications-panel__copy">
                        <span className="notifications-panel__text">
                          {t('notifications-panel-messages', {
                            count: item.unreadCount,
                            name: item.name,
                          })}
                        </span>
                        <span className="notifications-panel__meta">
                          {t('notifications-panel-open-chat')}
                        </span>
                      </span>
                      <span className="notifications-panel__pill">{item.unreadCount}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="notifications-panel__footer">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate('/my-swaps');
              }}
            >
              {t('notifications-panel-view-all')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
